/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetCostUsageTimeseriesParams,
  GetTokenUsageTimeseriesParams,
  GetUsageMetadataParams,
  ListUsageFilterGroupMembersParams,
  ListUsageFilterGroupMembersPathParameters,
  ListUsageFilterGroupsParams,
  TLCostUsageTimeseriesResponse,
  TLGroupMembersResponse,
  TLGroupsListResponse,
  TLHTTPValidationError,
  TLTokenUsageTimeseriesResponse,
  TLUsageMetadataResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Cost Usage Timeseries
 */
export const getCostUsageTimeseries = (
  params: GetCostUsageTimeseriesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCostUsageTimeseriesResponse>(
    { url: `/llm-usage/costs`, method: 'GET', params, signal },
    options,
  );
};

export const getGetCostUsageTimeseriesQueryKey = (params?: GetCostUsageTimeseriesParams) => {
  return ['llm-usage', 'costs', ...(params ? [params] : [])] as const;
};

export const getGetCostUsageTimeseriesQueryOptions = <
  TData = Awaited<ReturnType<typeof getCostUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetCostUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCostUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCostUsageTimeseriesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCostUsageTimeseries>>> = ({ signal }) =>
    getCostUsageTimeseries(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCostUsageTimeseries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCostUsageTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getCostUsageTimeseries>>>;
export type GetCostUsageTimeseriesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCostUsageTimeseries<
  TData = Awaited<ReturnType<typeof getCostUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetCostUsageTimeseriesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCostUsageTimeseries>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCostUsageTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getCostUsageTimeseries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCostUsageTimeseries<
  TData = Awaited<ReturnType<typeof getCostUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetCostUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCostUsageTimeseries>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCostUsageTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getCostUsageTimeseries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCostUsageTimeseries<
  TData = Awaited<ReturnType<typeof getCostUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetCostUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCostUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Cost Usage Timeseries
 */

export function useGetCostUsageTimeseries<
  TData = Awaited<ReturnType<typeof getCostUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetCostUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCostUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCostUsageTimeseriesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Cost Usage Timeseries
 */
export const prefetchGetCostUsageTimeseriesQuery = async <
  TData = Awaited<ReturnType<typeof getCostUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetCostUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCostUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCostUsageTimeseriesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Cost Usage Timeseries
 */
export const invalidateGetCostUsageTimeseries = async (
  queryClient: QueryClient,
  params: GetCostUsageTimeseriesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCostUsageTimeseriesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Usage Filter Groups
 */
export const listUsageFilterGroups = (
  params?: ListUsageFilterGroupsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupsListResponse>({ url: `/llm-usage/groups`, method: 'GET', params, signal }, options);
};

export const getListUsageFilterGroupsQueryKey = (params?: ListUsageFilterGroupsParams) => {
  return ['llm-usage', 'groups', ...(params ? [params] : [])] as const;
};

export const getListUsageFilterGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof listUsageFilterGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUsageFilterGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUsageFilterGroupsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listUsageFilterGroups>>> = ({ signal }) =>
    listUsageFilterGroups(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUsageFilterGroups>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUsageFilterGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listUsageFilterGroups>>>;
export type ListUsageFilterGroupsQueryError = ErrorType<TLHTTPValidationError>;

export function useListUsageFilterGroups<
  TData = Awaited<ReturnType<typeof listUsageFilterGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListUsageFilterGroupsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroups>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUsageFilterGroups>>,
          TError,
          Awaited<ReturnType<typeof listUsageFilterGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUsageFilterGroups<
  TData = Awaited<ReturnType<typeof listUsageFilterGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUsageFilterGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroups>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUsageFilterGroups>>,
          TError,
          Awaited<ReturnType<typeof listUsageFilterGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUsageFilterGroups<
  TData = Awaited<ReturnType<typeof listUsageFilterGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUsageFilterGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Usage Filter Groups
 */

export function useListUsageFilterGroups<
  TData = Awaited<ReturnType<typeof listUsageFilterGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUsageFilterGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListUsageFilterGroupsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Usage Filter Groups
 */
export const prefetchListUsageFilterGroupsQuery = async <
  TData = Awaited<ReturnType<typeof listUsageFilterGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListUsageFilterGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListUsageFilterGroupsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Usage Filter Groups
 */
export const invalidateListUsageFilterGroups = async (
  queryClient: QueryClient,
  params?: ListUsageFilterGroupsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListUsageFilterGroupsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Usage Filter Group Members
 */
export const listUsageFilterGroupMembers = (
  { groupId }: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupMembersResponse>(
    { url: `/llm-usage/groups/${groupId}/members`, method: 'GET', params, signal },
    options,
  );
};

export const getListUsageFilterGroupMembersQueryKey = (
  { groupId }: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
) => {
  return ['llm-usage', 'groups', groupId, 'members', ...(params ? [params] : [])] as const;
};

export const getListUsageFilterGroupMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUsageFilterGroupMembersQueryKey({ groupId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>> = ({ signal }) =>
    listUsageFilterGroupMembers({ groupId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUsageFilterGroupMembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUsageFilterGroupMembers>>
>;
export type ListUsageFilterGroupMembersQueryError = ErrorType<TLHTTPValidationError>;

export function useListUsageFilterGroupMembers<
  TData = Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListUsageFilterGroupMembersPathParameters,
  params: undefined | ListUsageFilterGroupMembersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof listUsageFilterGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUsageFilterGroupMembers<
  TData = Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof listUsageFilterGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUsageFilterGroupMembers<
  TData = Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Usage Filter Group Members
 */

export function useListUsageFilterGroupMembers<
  TData = Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListUsageFilterGroupMembersQueryOptions({ groupId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Usage Filter Group Members
 */
export const prefetchListUsageFilterGroupMembersQuery = async <
  TData = Awaited<ReturnType<typeof listUsageFilterGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsageFilterGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListUsageFilterGroupMembersQueryOptions({ groupId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Usage Filter Group Members
 */
export const invalidateListUsageFilterGroupMembers = async (
  queryClient: QueryClient,
  { groupId }: ListUsageFilterGroupMembersPathParameters,
  params?: ListUsageFilterGroupMembersParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListUsageFilterGroupMembersQueryKey({ groupId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Usage Metadata
 */
export const getUsageMetadata = (
  params: GetUsageMetadataParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUsageMetadataResponse>({ url: `/llm-usage/metadata`, method: 'GET', params, signal }, options);
};

export const getGetUsageMetadataQueryKey = (params?: GetUsageMetadataParams) => {
  return ['llm-usage', 'metadata', ...(params ? [params] : [])] as const;
};

export const getGetUsageMetadataQueryOptions = <
  TData = Awaited<ReturnType<typeof getUsageMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetUsageMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsageMetadataQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUsageMetadata>>> = ({ signal }) =>
    getUsageMetadata(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUsageMetadata>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsageMetadataQueryResult = NonNullable<Awaited<ReturnType<typeof getUsageMetadata>>>;
export type GetUsageMetadataQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUsageMetadata<
  TData = Awaited<ReturnType<typeof getUsageMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetUsageMetadataParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageMetadata>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsageMetadata>>,
          TError,
          Awaited<ReturnType<typeof getUsageMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsageMetadata<
  TData = Awaited<ReturnType<typeof getUsageMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetUsageMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageMetadata>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsageMetadata>>,
          TError,
          Awaited<ReturnType<typeof getUsageMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsageMetadata<
  TData = Awaited<ReturnType<typeof getUsageMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetUsageMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Usage Metadata
 */

export function useGetUsageMetadata<
  TData = Awaited<ReturnType<typeof getUsageMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetUsageMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUsageMetadataQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Usage Metadata
 */
export const prefetchGetUsageMetadataQuery = async <
  TData = Awaited<ReturnType<typeof getUsageMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetUsageMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUsageMetadataQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Usage Metadata
 */
export const invalidateGetUsageMetadata = async (
  queryClient: QueryClient,
  params: GetUsageMetadataParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUsageMetadataQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Token Usage Timeseries
 */
export const getTokenUsageTimeseries = (
  params: GetTokenUsageTimeseriesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTokenUsageTimeseriesResponse>(
    { url: `/llm-usage/tokens`, method: 'GET', params, signal },
    options,
  );
};

export const getGetTokenUsageTimeseriesQueryKey = (params?: GetTokenUsageTimeseriesParams) => {
  return ['llm-usage', 'tokens', ...(params ? [params] : [])] as const;
};

export const getGetTokenUsageTimeseriesQueryOptions = <
  TData = Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetTokenUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTokenUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTokenUsageTimeseriesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTokenUsageTimeseries>>> = ({ signal }) =>
    getTokenUsageTimeseries(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTokenUsageTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getTokenUsageTimeseries>>>;
export type GetTokenUsageTimeseriesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetTokenUsageTimeseries<
  TData = Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetTokenUsageTimeseriesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTokenUsageTimeseries>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getTokenUsageTimeseries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTokenUsageTimeseries<
  TData = Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetTokenUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTokenUsageTimeseries>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getTokenUsageTimeseries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTokenUsageTimeseries<
  TData = Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetTokenUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTokenUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Token Usage Timeseries
 */

export function useGetTokenUsageTimeseries<
  TData = Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetTokenUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTokenUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTokenUsageTimeseriesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Token Usage Timeseries
 */
export const prefetchGetTokenUsageTimeseriesQuery = async <
  TData = Awaited<ReturnType<typeof getTokenUsageTimeseries>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetTokenUsageTimeseriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTokenUsageTimeseries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTokenUsageTimeseriesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Token Usage Timeseries
 */
export const invalidateGetTokenUsageTimeseries = async (
  queryClient: QueryClient,
  params: GetTokenUsageTimeseriesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTokenUsageTimeseriesQueryKey(params) }, options);

  return queryClient;
};
